import type { Permission, RoleDefinition, UserRole } from '../types/core';

/**
 * ONE permission matrix, consulted by both the routing layer and individual
 * actions, so a permission is defined exactly once.
 *
 * SECURITY BOUNDARY WARNING: these checks demonstrate the access model. They run
 * in the browser and are therefore NOT a security boundary. Every gate here must
 * be duplicated server-side before launch.
 */

const candidatePermissions: Permission[] = [
  'opportunity.view_public',
  'application.create',
  'application.withdraw_own',
  'document.manage_own',
  'consent.manage_own',
  'privacy.request_own',
  'message.send_own',
];

const employerPermissions: Permission[] = [
  'opportunity.view_public',
  'mandate.request',
  'mandate.view_own',
  'presentation.view_authorized',
  'feedback.submit',
  'interview.request',
  'invoice.view_own',
  'message.send_own',
];

const recruiterPermissions: Permission[] = [
  'opportunity.view_public',
  'candidate.search',
  'candidate.view_full',
  'document.review',
  'match.view_scores',
  'application.advance',
  'application.record_adverse_decision',
  'presentation.request_authorization',
  'note.write_internal',
  'task.manage',
  'mandate.manage',
  'interview.manage',
  'report.view',
  'message.send_own',
];

const administratorPermissions: Permission[] = [
  ...recruiterPermissions,
  'user.manage',
  'employer.verify',
  'opportunity.verify',
  'opportunity.publish',
  'content.manage',
  'content.publish',
  'email_template.manage',
  'audit.view',
  'consent.view_all',
  'privacy.process',
  'analytics.view',
];

const superAdministratorPermissions: Permission[] = [
  ...administratorPermissions,
  'role.manage',
  'security.manage',
  'system.configure',
];

export const roleDefinitions: RoleDefinition[] = [
  {
    role: 'candidate',
    label: 'Candidate',
    description:
      'An executive registered in the network. Sees only their own records. Cannot see other candidates, mandates, internal notes or match scores.',
    permissions: candidatePermissions,
  },
  {
    role: 'employer',
    label: 'Employer',
    description:
      'A verified representative of a commissioning organization. Sees only candidates who have authorized presentation for that organization’s mandates.',
    permissions: employerPermissions,
  },
  {
    role: 'recruiter',
    label: 'Recruiter',
    description:
      'A search consultant. Can search candidates, review documents and advance applications. Explicitly CANNOT edit or replace a candidate-uploaded file.',
    permissions: recruiterPermissions,
  },
  {
    role: 'administrator',
    label: 'Administrator',
    description:
      'Operational administration: users, verification, content, governance records. Cannot alter roles, security settings or system configuration.',
    permissions: administratorPermissions,
  },
  {
    role: 'super_administrator',
    label: 'Super Administrator',
    description:
      'Full administration including role definitions, security settings and system configuration. Intended for a very small number of named individuals.',
    permissions: superAdministratorPermissions,
  },
];

const permissionsByRole: Record<UserRole, Set<Permission>> = roleDefinitions.reduce(
  (acc, def) => {
    acc[def.role] = new Set(def.permissions);
    return acc;
  },
  {} as Record<UserRole, Set<Permission>>,
);

export const can = (role: UserRole | undefined, permission: Permission): boolean =>
  role ? permissionsByRole[role].has(permission) : false;

export const permissionMatrix: { permission: Permission; roles: Record<UserRole, boolean> }[] = (() => {
  const all = new Set<Permission>();
  roleDefinitions.forEach((d) => d.permissions.forEach((p) => all.add(p)));
  // Explicitly include the deny-by-design permission so the matrix shows it as denied everywhere.
  all.add('document.edit_candidate_file');
  return Array.from(all)
    .sort()
    .map((permission) => ({
      permission,
      roles: {
        candidate: can('candidate', permission),
        employer: can('employer', permission),
        recruiter: can('recruiter', permission),
        administrator: can('administrator', permission),
        super_administrator: can('super_administrator', permission),
      },
    }));
})();

/** Which portal a signed-in role lands in. */
export const portalRootForRole = (role: UserRole): string => {
  switch (role) {
    case 'candidate':
      return '/portal/candidate';
    case 'employer':
      return '/portal/employer';
    case 'recruiter':
      return '/portal/recruiter';
    default:
      return '/portal/admin';
  }
};

export const permissionLabels: Partial<Record<Permission, string>> = {
  'document.edit_candidate_file':
    'Edit or replace a candidate-uploaded file — denied to every role by design. Documents are append-only.',
};
